import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaPlus, FaSort, FaSortUp, FaSortDown, FaSearch, FaDownload } from 'react-icons/fa';
import dayjs from 'dayjs';
import RFQItemsFormModal from './RFQItemsFormModal';
import { useScreenPermissions } from '../components/useScreenPermissions';
import RFQItemsViewModal from './RFQItemsViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path fill="currentColor" d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path fill="currentColor" d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/>
  </svg>
);

const API_URL = '/api';

const RFQItemsMaster = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const [showView, setShowView] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [paymentTerms, setPaymentTerms] = useState('All');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // -------- THEME ONLY: apply DepartmentMaster color variables + scoped overrides --------
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",    // primary accent (mint/teal)
      "--accent-hover": "#23756e",    // hover for primary buttons
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'rfq-master-mint-overrides';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        /* scope whole theme to the root container with class .rfq-master-root */
        .rfq-master-root { background-color: var(--page-bg) !important; }

        /* Primary buttons (blue) -> mint accent */
        .rfq-master-root .bg-blue-500 { background-color: var(--accent-color) !important; }
        .rfq-master-root .bg-blue-500:hover,
        .rfq-master-root .hover\\:bg-blue-400:hover { background-color: var(--accent-hover) !important; }

        /* White secondary button borders use page border color */
        .rfq-master-root .bg-white.border-slate-200,
        .rfq-master-root .border-slate-200 { border-color: var(--page-border-color) !important; }

        /* Table header uses mint header background (Department style) */
        .rfq-master-root main table thead { background-color: var(--table-header-bg) !important; color: var(--text-primary) !important; }

        /* Pagination active/inactive styling */
        .rfq-master-root .page-active { background-color: var(--accent-color) !important; color: #ffffff !important; border-radius: 4px; padding: 0.25rem 0.5rem; }
        .rfq-master-root .page-inactive { color: var(--accent-color) !important; }
        .rfq-master-root .page-inactive:hover { background-color: #f7fbfa !important; }

        /* Status badge fallbacks consistent with Department */
        .rfq-master-root .status-active { background-color: var(--status-active-bg); color: var(--status-active-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; font-weight: 700; display: inline-block; }
        .rfq-master-root .status-inactive { background-color: var(--status-inactive-bg); color: var(--status-inactive-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; font-weight: 700; display: inline-block; }

        /* Ensure sticky header shadow contrast when scrolled */
        .rfq-master-root .shadow { box-shadow: 0 1px 0 rgba(15,23,42,0.04) !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);
  // ---------------------------------------------------------------------------------------

  useEffect(() => {
    fetchRfqs();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchRfqs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/rfqs`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setRfqs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching RFQs:', error);
      setRfqs([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
    const sorted = [...rfqs].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      if (key === 'rfq_date') {
        return direction === 'asc'
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setRfqs(sorted);
  };

  const getSortIcon = (key) => {
     if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="ml-1 w-3 h-3" /> : <FaSortDown className="ml-1 w-3 h-3" />;
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleView = async (rfq) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/rfqs/${rfq.id}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setSelectedRFQ(data);
      setShowView(true);
    } catch (error) {
      console.error('Error fetching RFQ details:', error);
      setSelectedRFQ(null);
      setShowView(true);
    }
    setLoading(false);
  };

  const handleEdit = async (rfq) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/rfqs/${rfq.id}`);
      if (!res.ok) 
        throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setSelectedRFQ(data);
      setIsEdit(true);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching RFQ details:', error);
      setSelectedRFQ(null);
      setIsEdit(true);
      setShowForm(true);
    }
    setLoading(false);
  };

  const handleCreate = () => {
    setSelectedRFQ(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleDownloadCSV = () => {
     const headers = [
      'RFQ No', 'RFQ Date', 'PR No', 'Vendor Code', 'Payment Terms', 'Vendor Name', 'Status'
    ];
    const rows = rfqs.map(r => [
      r.rfq_no,
      r.rfq_date ? dayjs(r.rfq_date).format('YYYY-MM-DD') : '-',
      r.pr_no,
      r.vendor_code,
      r.payment_terms,
      r.vendor_name,
      r.status
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rfqs.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredRfqs = rfqs.filter(rfq => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = term === '' ||
      (rfq.rfq_no || '').toString().toLowerCase().includes(term) ||
      (rfq.pr_no || '').toString().toLowerCase().includes(term) ||
      (rfq.vendor_code || '').toString().toLowerCase().includes(term) ||
      (rfq.vendor_name || '').toString().toLowerCase().includes(term);
    const matchesStatus = status === 'All' || rfq.status === status;
    const matchesPaymentTerms = paymentTerms === 'All' || rfq.payment_terms === paymentTerms;
    return matchesSearch && matchesStatus && matchesPaymentTerms;
  });

  // pagination + sorting
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
      if (sortConfig.key === 'rfq_date') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else {
        aVal = (aVal ?? '').toString().toLowerCase();
        bVal = (bVal ?? '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedRfqs = getSortedData(filteredRfqs);
  const totalPages = Math.ceil(sortedRfqs.length / itemsPerPage);
  const rfqsForPage = sortedRfqs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className="p-1  hover:bg-slate-100 disabled:text-slate-300"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-3 h-3 text-slate-700" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1  transition-colors ${currentPage === i ? 'bg-blue-500 text-white font-semibold' : 'text-slate-800 hover:bg-blue-400'}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className="p-1 rounded-md hover:bg-slate-100 disabled:text-slate-300"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-3 h-3 text-slate-700" />
      </button>
    );

    return pages;
  };

  const paymentTermsOptions = [...new Set(rfqs.map(r => r.payment_terms).filter(Boolean))];

  // reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, status, paymentTerms]);

  // permissions
  const canCreate = hasFullAccess || hasPermission('MRN_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('MRN_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('MRN_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Sidebar theme to match page accent/profile color (keeps Sidebar consistent across pages)
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex bg-slate-50 rfq-master-root">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} 
      onLogout={handleLogout}/>
      <main className={`flex-1 p-8 overflow-hidden ml-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>

        {/* Header Section */}
        <div className="mb-8 border-b border-slate-300 pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">RFQ Master</h1>
              <p className="text-slate-600">Manage your Request for Quotation (RFQ) items</p>
            </div>
            {/* Controls (kept commented like original) */}
            {/* <div className="flex items-center gap-3"> ... </div> */}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-sm shadow-md border border-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Total RFQs</p>
                <p className="text-2xl font-bold text-slate-900">{rfqs.length}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-sm bg-sky-100 text-sky-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-sm shadow-md border border-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Active RFQs</p>
                <p className="text-2xl font-bold text-slate-900">{rfqs.filter(r => r.status === 'Active').length}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-sm bg-emerald-100 text-emerald-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-sm shadow-md border border-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">Pending RFQs</p>
                <p className="text-2xl font-bold text-slate-900">{rfqs.filter(r => r.status === 'Pending').length}</p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-sm bg-amber-100 text-amber-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-sm shadow-md border border-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-1">This Month</p>
                <p className="text-2xl font-bold text-slate-900">
                  {rfqs.filter(r => {
                    const createdDate = r.rfq_date ? new Date(r.rfq_date) : null;
                    if (!createdDate) return false;
                    const now = new Date();
                    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-sm bg-violet-100 text-violet-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white p-6 rounded-sm shadow-md mb-6 border border-gray-300">
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-slate-700 text-sm font-medium mb-2">What are you looking for?</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search for RFQ number, PR number, vendor, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-200 transition-all duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-48 py-2 px-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-200 transition-all duration-200"
              >
                <option>All</option>
                <option>Active</option>
                <option>Pending</option>
                <option>Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">Payment Terms</label>
              <select
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-48 py-2 px-3 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-teal-200 transition-all duration-200"
              >
                <option>All</option>
                {paymentTermsOptions.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>

            <button className="bg-blue-500 hover:bg-blue-400 text-white font-semibold px-6 py-2 rounded-sm shadow transition-all duration-200">
              Search
            </button>
          </div>
        </div>

        {/* RFQs Table */}
        <div className="flex-1 overflow-hidden bg-white  rounded-sm shadow-lg border border-gray-300">
          <div className="flex justify-between items-center p-4">
            <h2 className="text-xl font-bold text-slate-900">RFQ Summary</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-slate-600 ">
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          {/* --- ATTACHED: table header now sits directly under the white block (no extra gap / border-top) --- */}
          <div className="overflow-x-auto h-full scroll-smooth">
            <table className="w-full text-sm">
              <thead className="bg-gray-200 text-left text-gray-600 text-xs font-semibold uppercase">
                <tr>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('rfq_no')}>
                    <div className="flex items-center">RFQ NO. {getSortIcon('rfq_no')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('rfq_date')}>
                    <div className="flex items-center">DATE {getSortIcon('rfq_date')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('pr_no')}>
                    <div className="flex items-center">PR NO. {getSortIcon('pr_no')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('vendor_code')}>
                    <div className="flex items-center">VENDOR CODE {getSortIcon('vendor_code')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('payment_terms')}>
                    <div className="flex items-center">PAYMENT TERMS {getSortIcon('payment_terms')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('vendor_name')}>
                    <div className="flex items-center">VENDOR NAME {getSortIcon('vendor_name')}</div>
                  </th>
                  <th className={`px-6 py-3 text-left cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${isScrolled ? 'shadow-lg' : ''}`} onClick={() => handleSort('status')}>
                    <div className="flex items-center">STATUS {getSortIcon('status')}</div>
                  </th>
                  <th className={`px-6 py-3 text-center ${isScrolled ? 'shadow-lg' : ''}`}>ACTIONS</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-600 font-semibold">Loading RFQs...</td>
                  </tr>
                ) : rfqsForPage.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-600 font-semibold">No RFQs found.</td>
                  </tr>
                ) : (
                  rfqsForPage.map((rfq) => (
                    <tr key={rfq.id} className="hover:bg-slate-50 transition-colors duration-200 text-left">
                      <td className="px-6 py-3 font-medium text-slate-900">{rfq.rfq_no}</td>
                      <td className="px-6 py-3 text-slate-600">{rfq.rfq_date ? dayjs(rfq.rfq_date).format('DD MMM YYYY') : '-'}</td>
                      <td className="px-6 py-3 text-slate-600 font-semibold">{rfq.pr_no}</td>
                      <td className="px-6 py-3 text-slate-600">{(rfq.vendor_code || '').toString().toUpperCase()}</td>
                      <td className="px-6 py-3 text-slate-600">{(rfq.payment_terms || '').toString().toUpperCase()}</td>
                      <td className="px-6 py-3 text-slate-600">{(rfq.vendor_name || '').toString().toUpperCase()}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase
                          ${rfq.status === 'Active' ? 'bg-emerald-600 text-white' :
                            rfq.status === 'Pending' ? 'bg-amber-500 text-white' :
                            'bg-rose-600 text-white'}`}>
                          {rfq.status}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2 justify-center items-center">
                          {canView && (
                            <button title="View" className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-all duration-200" onClick={() => handleView(rfq)}>
                              <FaEye />
                            </button>
                          )}
                          {canEdit && (
                            <button title="Edit" className="p-2 rounded-full text-teal-700 hover:text-teal-900 transition-all duration-200" onClick={() => handleEdit(rfq)}>
                              <FaEdit />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <RFQItemsFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={() => {
            setShowForm(false);
            fetchRfqs();
          }}
          initialData={selectedRFQ}
          isEdit={isEdit}
          loading={formLoading}
        />

        <RFQItemsViewModal
          open={showView}
          onClose={() => setShowView(false)}
          rfqId={selectedRFQ ? selectedRFQ.id : null}
        />
      </main>
    </div>
  );
};

export default RFQItemsMaster;
